// Turns one scene node into vertices.
//
// Everything is emitted in the coordinate space of the batch the node belongs to:
// the node's own transform and those of its ancestors up to the batch root are
// already folded into `matrix`. The camera is not — that stays a uniform, so an
// object moving across the map costs nothing here.

import { applyX, applyY, meanScale } from "../scene/matrix";
import { parseLength, withAlpha, COLOUR_NONE } from "../scene/style";
import { flattenPath } from "./path";
import { triangulate, strokePolyline, applyDashes } from "./tess";
import {
  fontKeyFor,
  fontMetrics,
  baselineOffset,
  layoutText,
  getGlyph,
} from "./text";
import { STATE_READY } from "./images";
import {
  MODE_SOLID,
  MODE_CIRCLE,
  MODE_GLYPH,
  MODE_IMAGE,
  MODE_PULSE,
  MODE_PARTICLE,
  PARTICLE_SEED_RANGE,
} from "./modes";
import { effects } from "../effects";

// Scratch array reused for subpaths built on the fly (rectangle outlines, lines,
// dashed circles) which have no stable identity to cache against.
const strokeScratch = [];

// Tessellation is memoised on the subpath objects that `flattenPath` caches, so
// the repeated shapes in the estate — every rounded tile of a given size shares
// one `d` string — are triangulated and offset once for the whole map, not once
// per object and certainly not once per frame.
function cachedTriangulate(subpath) {
  if (!subpath.fillIndices) subpath.fillIndices = triangulate(subpath.points);
  return subpath.fillIndices;
}

function cachedStroke(subpath, style) {
  const key =
    style.strokeWidth +
    "|" +
    (style.strokeLinecap || "butt") +
    "|" +
    (style.strokeDasharray ? style.strokeDasharray.join(",") : "");

  let cache = subpath.strokes;
  if (!cache) cache = subpath.strokes = new Map();

  let triangles = cache.get(key);
  if (triangles) return triangles;

  const out = [];
  strokeInto(out, subpath.points, subpath.closed, style);
  triangles = Float64Array.from(out);

  // A handful of shapes are rebuilt from live data every second — the graph
  // traces and the Redis memory arc — and each gets its own `d`, so the entry
  // count is bounded by flattenPath's own cache rather than growing here.
  cache.set(key, triangles);
  return triangles;
}

function strokeInto(out, points, closed, style) {
  const cap = style.strokeLinecap || "butt";
  if (style.strokeDasharray) {
    const runs = applyDashes(points, closed, style.strokeDasharray);
    for (let r = 0; r < runs.length; r++)
      strokePolyline(runs[r], false, style.strokeWidth, cap, out);
  } else {
    strokePolyline(points, closed, style.strokeWidth, cap, out);
  }
}

export function emitNode(node, matrix, style, ctx, out) {
  switch (node.tagName) {
    case "circle":
      emitCircle(node, matrix, style, out);
      break;
    case "rect":
      emitRect(node, matrix, style, out);
      break;
    case "line":
      emitLine(node, matrix, style, out);
      break;
    case "path":
      emitPath(node, matrix, style, out);
      break;
    case "text":
      emitText(node, matrix, style, ctx, out);
      break;
    case "image":
      emitImage(node, matrix, style, ctx, out);
      break;
    // A tag of the map's own, for an effect that has no SVG equivalent. Under the
    // SVG backend the browser makes an unknown element that draws nothing, which
    // is exactly the right fallback.
    case "particles":
      emitParticles(node, matrix, style, out);
      break;
    default:
      break;
  }
}

// Tags whose children are drawn as part of the normal walk. Anything else is a
// leaf as far as drawing is concerned — a <text>'s children are laid out by the
// text emitter, and reference content never paints where it sits.
export function drawsChildren(node) {
  const tag = node.tagName;
  return (
    tag !== "text" && tag !== "defs" && tag !== "clipPath" && !node.nonRendering
  );
}

// ---------------------------------------------------------------------------
// Effects
// ---------------------------------------------------------------------------

// A plume. Every particle is the same quad with a different seed, and the shader
// works out where each one has got to from the clock — so the whole thing is one
// draw and stays still in the vertex buffer between rebuilds.
function emitParticles(node, matrix, style, out) {
  if (!effects.particles) return;

  const attrs = node.attrs;
  const count = Math.min(240, Math.max(0, num(attrs["data-count"], 0)));
  const size = num(attrs["data-size"], 4);
  const intensity = num(attrs["data-intensity"], 0);
  if (count <= 0 || size <= 0 || intensity <= 0) return;

  const colour = withAlpha(style.fill, style.opacity * intensity);
  if (colour === COLOUR_NONE) return;

  for (let i = 0; i < count; i++) {
    // Spread evenly through the cycle so the plume is continuous rather than
    // arriving in puffs, and given a seed that does not repeat across emitters.
    const phase = (i + 0.5) / count;
    const seed = (i * 37) % PARTICLE_SEED_RANGE;

    pushQuad(
      out,
      matrix,
      0,
      0,
      size,
      colour,
      MODE_PARTICLE,
      // Whole part carries the seed, fraction the phase. Nudged off a whole
      // number so the fraction cannot round to zero and collapse the plume.
      seed + Math.min(0.999, phase)
    );
  }
}

// Two triangles centred on (cx, cy), with UV running -1..1 across them.
function pushQuad(out, matrix, cx, cy, half, colour, mode, param) {
  const x0 = cx - half;
  const x1 = cx + half;
  const y0 = cy - half;
  const y1 = cy + half;

  out.ensure(6);
  const corner = (x, y, u, v) =>
    out.push(
      applyX(matrix, x, y),
      applyY(matrix, x, y),
      u,
      v,
      colour,
      mode,
      param
    );

  corner(x0, y0, -1, -1);
  corner(x1, y0, 1, -1);
  corner(x1, y1, 1, 1);

  corner(x0, y0, -1, -1);
  corner(x1, y1, 1, 1);
  corner(x0, y1, -1, 1);
}

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

function emitCircle(node, matrix, style, out) {
  const attrs = node.attrs;
  const cx = num(attrs.cx, 0);
  const cy = num(attrs.cy, 0);
  const r = num(attrs.r, 0);
  if (r <= 0) return;

  const fill = withAlpha(style.fill, style.opacity);
  const stroke = withAlpha(style.stroke, style.opacity);
  const strokeWidth = style.strokeWidth;
  if (fill !== COLOUR_NONE) pushDisc(out, matrix, cx, cy, r, 0, fill);

  if (stroke !== COLOUR_NONE && strokeWidth > 0) {
    if (style.strokeDasharray) {
      // A dashed outline is no longer a disc, so it goes through the generic
      // stroke path as a flattened circle.
      strokeSubpaths(
        out,
        matrix,
        circleSubpath(cx, cy, r, matrix),
        style,
        stroke,
        false
      );
    } else {
      const outer = r + strokeWidth / 2;
      const inner = Math.max(0, r - strokeWidth / 2);
      pushDisc(out, matrix, cx, cy, outer, inner / outer, stroke);
    }
  }
}

// A quad carrying the -1..1 coordinates the fragment shader turns into a disc or
// ring. Six vertices regardless of radius, and exact at any zoom.
function pushDisc(out, matrix, cx, cy, radius, innerFraction, colour) {
  const mode = MODE_CIRCLE;

  out.ensure(6);

  const x0 = cx - radius;
  const y0 = cy - radius;
  const x1 = cx + radius;
  const y1 = cy + radius;

  const ax = applyX(matrix, x0, y0);
  const ay = applyY(matrix, x0, y0);
  const bx = applyX(matrix, x1, y0);
  const by = applyY(matrix, x1, y0);
  const dx = applyX(matrix, x1, y1);
  const dy = applyY(matrix, x1, y1);
  const ex = applyX(matrix, x0, y1);
  const ey = applyY(matrix, x0, y1);

  out.push(ax, ay, -1, -1, colour, mode, innerFraction);
  out.push(bx, by, 1, -1, colour, mode, innerFraction);
  out.push(dx, dy, 1, 1, colour, mode, innerFraction);
  out.push(ax, ay, -1, -1, colour, mode, innerFraction);
  out.push(dx, dy, 1, 1, colour, mode, innerFraction);
  out.push(ex, ey, -1, 1, colour, mode, innerFraction);
}

function circleSubpath(cx, cy, r, matrix) {
  const steps = Math.max(
    24,
    Math.min(256, Math.ceil(r * meanScale(matrix) * 0.9))
  );
  const points = [];
  for (let i = 0; i < steps; i++) {
    const a = (Math.PI * 2 * i) / steps;
    points.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  return [{ points, closed: true }];
}

function emitRect(node, matrix, style, out) {
  const attrs = node.attrs;
  const x = num(attrs.x, 0);
  const y = num(attrs.y, 0);
  const width = num(attrs.width, 0);
  const height = num(attrs.height, 0);
  if (width <= 0 || height <= 0) return;

  const fill = withAlpha(style.fill, style.opacity);
  if (fill !== COLOUR_NONE) {
    out.ensure(6);
    pushTri(out, matrix, x, y, x + width, y, x + width, y + height, fill);
    pushTri(out, matrix, x, y, x + width, y + height, x, y + height, fill);
  }

  const stroke = withAlpha(style.stroke, style.opacity);
  if (stroke !== COLOUR_NONE && style.strokeWidth > 0) {
    const points = [x, y, x + width, y, x + width, y + height, x, y + height];
    strokeSubpaths(
      out,
      matrix,
      [{ points, closed: true }],
      style,
      stroke,
      false
    );
  }
}

function emitLine(node, matrix, style, out) {
  const stroke = withAlpha(style.stroke, style.opacity);
  if (stroke === COLOUR_NONE || style.strokeWidth <= 0) return;

  const attrs = node.attrs;
  const x1 = num(attrs.x1, 0);
  const y1 = num(attrs.y1, 0);
  const x2 = num(attrs.x2, 0);
  const y2 = num(attrs.y2, 0);

  strokeSubpaths(
    out,
    matrix,
    [{ points: [x1, y1, x2, y2], closed: false }],
    style,
    stroke,
    false,
    flowOf(node, x1, y1, x2, y2)
  );
}

// A connection line's traffic, if the effect is on and the line is carrying any.
//
// The parameter runs from the child end to the parent end, so pulses travel
// inwards — work happens out at the roles and reports back towards the root.
// `layout/index.js` writes the line with the child at (x1, y1), so that is the
// origin.
function flowOf(node, x1, y1, x2, y2) {
  if (!effects.trafficPulses) return null;

  const busy = num(node.attrs["data-flow"], 0);
  if (!(busy > 0)) return null;

  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared < 1e-6) return null;

  return { x: x1, y: y1, dx, dy, scale: 1 / lengthSquared, busy };
}

function emitPath(node, matrix, style, out) {
  const subpaths = flattenPath(node.attrs.d);
  if (!subpaths.length) return;

  const fill = withAlpha(style.fill, style.opacity);
  if (fill !== COLOUR_NONE) {
    for (let i = 0; i < subpaths.length; i++) {
      const points = subpaths[i].points;
      const indices = cachedTriangulate(subpaths[i]);
      out.ensure(indices.length);
      for (let t = 0; t < indices.length; t += 3) {
        pushTri(
          out,
          matrix,
          points[indices[t] * 2],
          points[indices[t] * 2 + 1],
          points[indices[t + 1] * 2],
          points[indices[t + 1] * 2 + 1],
          points[indices[t + 2] * 2],
          points[indices[t + 2] * 2 + 1],
          fill
        );
      }
    }
  }

  const stroke = withAlpha(style.stroke, style.opacity);
  if (stroke !== COLOUR_NONE && style.strokeWidth > 0)
    strokeSubpaths(out, matrix, subpaths, style, stroke);
}

// `cacheable` is false for outlines synthesised here — a rectangle's border, a
// line, a dashed circle — which have no persistent subpath object to hang the
// result on and are cheap enough to redo.
function strokeSubpaths(
  out,
  matrix,
  subpaths,
  style,
  colour,
  cacheable = true,
  flow = null
) {
  for (let i = 0; i < subpaths.length; i++) {
    const subpath = subpaths[i];

    if (cacheable) {
      flushTriangles(out, matrix, cachedStroke(subpath, style), colour, flow);
    } else {
      strokeScratch.length = 0;
      strokeInto(strokeScratch, subpath.points, subpath.closed, style);
      flushTriangles(out, matrix, strokeScratch, colour, flow);
    }
  }
}

// `flow` turns the run into a traffic link: it carries the segment the stroke was
// built from, so each vertex can be given its position along it, and how busy the
// link is. Measured before the matrix is applied, because both the segment and
// the vertices are still in the same local space at that point.
function flushTriangles(out, matrix, flat, colour, flow) {
  const count = flat.length / 2;
  out.ensure(count);

  const mode = flow ? MODE_PULSE : MODE_SOLID;
  const busy = flow ? flow.busy : 0;

  for (let i = 0; i < flat.length; i += 2) {
    const x = flat[i];
    const y = flat[i + 1];

    let along = 0;
    if (flow) {
      along = ((x - flow.x) * flow.dx + (y - flow.y) * flow.dy) * flow.scale;
      if (along < 0) along = 0;
      else if (along > 1) along = 1;
    }

    out.push(
      applyX(matrix, x, y),
      applyY(matrix, x, y),
      along,
      0,
      colour,
      mode,
      busy
    );
  }
}

function pushTri(out, matrix, ax, ay, bx, by, cx, cy, colour) {
  out.push(
    applyX(matrix, ax, ay),
    applyY(matrix, ax, ay),
    0,
    0,
    colour,
    MODE_SOLID,
    0
  );
  out.push(
    applyX(matrix, bx, by),
    applyY(matrix, bx, by),
    0,
    0,
    colour,
    MODE_SOLID,
    0
  );
  out.push(
    applyX(matrix, cx, cy),
    applyY(matrix, cx, cy),
    0,
    0,
    colour,
    MODE_SOLID,
    0
  );
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

function emitText(node, matrix, style, ctx, out) {
  const textPath = findTextPath(node);
  if (textPath) {
    emitTextPath(node, textPath, matrix, style, ctx, out);
    return;
  }

  const content = node.textContent;
  if (!content) return;

  const size = style.fontSize;
  if (!(size > 0)) return;

  const key = fontKeyFor(style);
  const metrics = fontMetrics(key);
  const layout = layoutText(content, key);
  const spacing = style.letterSpacing || 0;

  const advance = layout.width * size + spacing * layout.clusters.length;

  let penX = num(node.attrs.x, 0);
  if (style.textAnchor === "middle") penX -= advance / 2;
  else if (style.textAnchor === "end") penX -= advance;

  const penY =
    num(node.attrs.y, 0) +
    baselineOffset(style.dominantBaseline, metrics) * size;

  emitGlyphRun(
    layout,
    key,
    size,
    spacing,
    penX,
    penY,
    matrix,
    style,
    ctx,
    out,
    1
  );
}

function emitGlyphRun(
  layout,
  fontKey,
  size,
  spacing,
  penX,
  penY,
  matrix,
  style,
  ctx,
  out,
  advanceScale
) {
  const fill = withAlpha(style.fill, style.opacity);

  // A stroke on text is only ever used here to embolden it, always in the same
  // colour as the fill, so it is folded into the distance field threshold rather
  // than outlining each glyph separately.
  const threshold = glyphThreshold(style, size);

  for (let i = 0; i < layout.clusters.length; i++) {
    const glyph = getGlyph(layout.clusters[i], fontKey, ctx.atlas);
    if (!glyph) continue;

    const x = penX + (layout.positions[i] * size + spacing * i) * advanceScale;
    pushGlyphQuad(out, matrix, glyph, x, penY, size, fill, threshold);
  }
}

function glyphThreshold(style, size) {
  if (style.stroke === COLOUR_NONE || !style.strokeWidth) return 0.5;
  // The field spans 16 raster pixels across a 64px em, so half the stroke width
  // in em units maps onto the field at this rate.
  const emOffset = style.strokeWidth / 2 / size;
  const shift = emOffset / (16 / 64);
  return Math.max(0.05, 0.5 - shift);
}

function pushGlyphQuad(out, matrix, glyph, penX, penY, size, colour, param) {
  const x0 = penX + glyph.x0 * size;
  const y0 = penY + glyph.y0 * size;
  const x1 = penX + glyph.x1 * size;
  const y1 = penY + glyph.y1 * size;

  const slot = glyph.slot;
  const mode = glyph.mode === MODE_IMAGE ? MODE_IMAGE : MODE_GLYPH;

  out.ensure(6);
  pushTexturedQuad(
    out,
    matrix,
    x0,
    y0,
    x1,
    y1,
    slot.u0,
    slot.v0,
    slot.u1,
    slot.v1,
    colour,
    mode,
    param
  );
}

function findTextPath(node) {
  for (let i = 0; i < node.children.length; i++) {
    if (node.children[i].tagName === "textPath") return node.children[i];
  }
  return null;
}

// Text following a referenced path. One site uses this — the ring of hostname
// around a Redis host — with `textLength` stretching the run to fill the arc.
function emitTextPath(textNode, textPathNode, matrix, style, ctx, out) {
  const content = textPathNode.textContent;
  if (!content) return;

  const href = textPathNode.attrs.href || textPathNode.attrs["xlink:href"];
  if (!href || href[0] !== "#") return;

  const target = ctx.lookupId(href.slice(1));
  if (!target) return;

  const subpaths = flattenPath(target.attrs.d);
  if (!subpaths.length) return;

  const polyline = subpaths[0].points;
  const cumulative = arcLengths(polyline);
  const totalLength = cumulative[cumulative.length - 1];
  if (!(totalLength > 0)) return;

  const size = style.fontSize;
  const key = fontKeyFor(style);
  const metrics = fontMetrics(key);
  const layout = layoutText(content, key);
  const spacing = style.letterSpacing || 0;

  const naturalAdvance = layout.width * size + spacing * layout.clusters.length;

  const textLength = parseLength(textNode.attrs.textLength, null, size);
  const scale =
    textLength && naturalAdvance > 0 ? textLength / naturalAdvance : 1;
  const advance = naturalAdvance * scale;

  let start = 0;
  if (style.textAnchor === "middle") start = (totalLength - advance) / 2;
  else if (style.textAnchor === "end") start = totalLength - advance;

  const baseline = baselineOffset(style.dominantBaseline, metrics) * size;
  const fill = withAlpha(style.fill, style.opacity);
  const threshold = glyphThreshold(style, size);

  for (let i = 0; i < layout.clusters.length; i++) {
    const glyph = getGlyph(layout.clusters[i], key, ctx.atlas);
    if (!glyph) continue;

    // Glyphs are placed by their midpoint so a curve rotates them about their
    // own centre, as SVG does.
    const half =
      i + 1 < layout.positions.length
        ? ((layout.positions[i + 1] - layout.positions[i]) * size) / 2
        : (layout.width * size - layout.positions[i] * size) / 2;

    const distance =
      start + (layout.positions[i] * size + spacing * i) * scale + half * scale;
    if (distance < 0 || distance > totalLength) continue;

    const at = pointAt(polyline, cumulative, distance);

    // Compose the glyph's own placement with the frame the path defines at that
    // point: x along the tangent, y along the normal.
    const frame = [at.tx, at.ty, -at.ty, at.tx, at.x, at.y];
    const combined = [
      matrix[0] * frame[0] + matrix[2] * frame[1],
      matrix[1] * frame[0] + matrix[3] * frame[1],
      matrix[0] * frame[2] + matrix[2] * frame[3],
      matrix[1] * frame[2] + matrix[3] * frame[3],
      matrix[0] * frame[4] + matrix[2] * frame[5] + matrix[4],
      matrix[1] * frame[4] + matrix[3] * frame[5] + matrix[5],
    ];

    pushGlyphQuad(
      out,
      combined,
      glyph,
      -half * scale,
      baseline,
      size,
      fill,
      threshold
    );
  }
}

function arcLengths(points) {
  const out = new Float64Array(points.length / 2);
  let total = 0;
  out[0] = 0;
  for (let i = 2; i < points.length; i += 2) {
    total += Math.hypot(
      points[i] - points[i - 2],
      points[i + 1] - points[i - 1]
    );
    out[i / 2] = total;
  }
  return out;
}

function pointAt(points, cumulative, distance) {
  let i = 1;
  while (i < cumulative.length - 1 && cumulative[i] < distance) i++;

  const segStart = cumulative[i - 1];
  const segLength = cumulative[i] - segStart || 1;
  const t = (distance - segStart) / segLength;

  const ax = points[(i - 1) * 2];
  const ay = points[(i - 1) * 2 + 1];
  const bx = points[i * 2];
  const by = points[i * 2 + 1];

  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;

  return {
    x: ax + dx * t,
    y: ay + dy * t,
    tx: dx / len,
    ty: dy / len,
  };
}

// ---------------------------------------------------------------------------
// Images
// ---------------------------------------------------------------------------

function emitImage(node, matrix, style, ctx, out) {
  const attrs = node.attrs;
  const href = attrs.href || attrs["xlink:href"];
  if (!href) return;

  const entry = ctx.images.get(href);
  if (!entry || entry.state !== STATE_READY || !entry.slot) return;

  const x = num(attrs.x, 0);
  const y = num(attrs.y, 0);
  const width = num(attrs.width, 0);
  const height = num(attrs.height, 0);
  if (width <= 0 || height <= 0) return;

  // SVG's default preserveAspectRatio is xMidYMid meet: fit inside the box and
  // centre what is left over.
  const boxAspect = width / height;
  let drawWidth = width;
  let drawHeight = height;
  if (entry.aspect > boxAspect) drawHeight = width / entry.aspect;
  else drawWidth = height * entry.aspect;

  const ox = x + (width - drawWidth) / 2;
  const oy = y + (height - drawHeight) / 2;

  const colour = withAlpha(0xffffffff, style.opacity);
  const slot = entry.slot;

  out.ensure(6);
  pushTexturedQuad(
    out,
    matrix,
    ox,
    oy,
    ox + drawWidth,
    oy + drawHeight,
    slot.u0,
    slot.v0,
    slot.u1,
    slot.v1,
    colour,
    MODE_IMAGE,
    0
  );
}

function pushTexturedQuad(
  out,
  matrix,
  x0,
  y0,
  x1,
  y1,
  u0,
  v0,
  u1,
  v1,
  colour,
  mode,
  param
) {
  const ax = applyX(matrix, x0, y0);
  const ay = applyY(matrix, x0, y0);
  const bx = applyX(matrix, x1, y0);
  const by = applyY(matrix, x1, y0);
  const cx = applyX(matrix, x1, y1);
  const cy = applyY(matrix, x1, y1);
  const dx = applyX(matrix, x0, y1);
  const dy = applyY(matrix, x0, y1);

  out.push(ax, ay, u0, v0, colour, mode, param);
  out.push(bx, by, u1, v0, colour, mode, param);
  out.push(cx, cy, u1, v1, colour, mode, param);
  out.push(ax, ay, u0, v0, colour, mode, param);
  out.push(cx, cy, u1, v1, colour, mode, param);
  out.push(dx, dy, u0, v1, colour, mode, param);
}

function num(value, fallback) {
  if (value === undefined || value === null || value === "") return fallback;
  const n = parseFloat(value);
  return isNaN(n) ? fallback : n;
}

export { num };
